/**
 * MEET HAUS — theme.js
 * Vanilla JS, ES6+. No jQuery. No external libraries.
 */

'use strict';

/* ─── Utilities ─────────────────────────────────────────── */

function formatMoney(cents, currencySymbol = '$') {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
    cents = parseInt(cents, 10);
  }
  if (isNaN(cents)) return currencySymbol + '0.00';
  const dollars = cents / 100;
  return currencySymbol + dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function debounce(fn, wait = 250) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

function domReady(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn);
  } else {
    fn();
  }
}

function trapFocus(container) {
  const focusable = container.querySelectorAll(
    'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
  );
  if (!focusable.length) return;
  const first = focusable[0];
  const last  = focusable[focusable.length - 1];

  function handleTab(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  container.addEventListener('keydown', handleTab);
  first.focus();
  return () => container.removeEventListener('keydown', handleTab);
}

function showNotification(message, duration = 3000) {
  let el = document.querySelector('.notification');
  if (!el) {
    el = document.createElement('div');
    el.className = 'notification';
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.classList.add('is-visible');
  setTimeout(() => el.classList.remove('is-visible'), duration);
}

/* ─── Cart State ────────────────────────────────────────── */

let cartData = null;
let releaseTrapFocus = null;

async function fetchCart() {
  const res = await fetch('/cart.js', {
    headers: { 'Content-Type': 'application/json' }
  });
  if (!res.ok) throw new Error('Could not fetch cart');
  cartData = await res.json();
  return cartData;
}

async function addToCart(variantId, quantity = 1, properties = {}) {
  const payload = {
    items: [{
      id: variantId,
      quantity,
      properties
    }]
  };
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.description || 'Could not add to cart');
  }
  const data = await res.json();
  cartData = await fetchCart();
  return data;
}

async function changeCartItem(key, quantity) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity })
  });
  if (!res.ok) throw new Error('Could not update cart');
  cartData = await res.json();
  return cartData;
}

async function removeCartItem(key) {
  return changeCartItem(key, 0);
}

/* ─── Cart Drawer ────────────────────────────────────────── */

function getCartDrawer() {
  return document.getElementById('cart-drawer');
}

function getCartBackdrop() {
  return document.getElementById('cart-drawer-backdrop');
}

function openCartDrawer() {
  const drawer   = getCartDrawer();
  const backdrop = getCartBackdrop();
  if (!drawer || !backdrop) return;
  document.body.style.overflow = 'hidden';
  backdrop.classList.add('is-open');
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  renderCartDrawer();
  releaseTrapFocus = trapFocus(drawer);
}

function closeCartDrawer() {
  const drawer   = getCartDrawer();
  const backdrop = getCartBackdrop();
  if (!drawer || !backdrop) return;
  document.body.style.overflow = '';
  backdrop.classList.remove('is-open');
  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  if (releaseTrapFocus) {
    releaseTrapFocus();
    releaseTrapFocus = null;
  }
}

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach(badge => {
    // Never set textContent on body — only update badge elements
    if (badge === document.body || badge.tagName === 'BODY') return;
    badge.textContent = count;
    badge.setAttribute('data-count', count);
  });
}

function renderCartDrawer() {
  const drawer = getCartDrawer();
  if (!drawer || !cartData) return;

  const FREE_SHIPPING_THRESHOLD = parseInt(
    document.body.getAttribute('data-free-shipping-threshold') || '7500'
  );

  const itemCount = cartData.item_count;
  const subtotal  = cartData.total_price;
  const items     = cartData.items;
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress  = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  updateCartCount(itemCount);

  // Build items HTML
  let itemsHTML = '';
  if (items.length === 0) {
    itemsHTML = `
      <div class="cart-drawer__empty">
        <svg class="cart-drawer__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <path d="M6 2 3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
          <line x1="3" y1="6" x2="21" y2="6"/>
          <path d="M16 10a4 4 0 01-8 0"/>
        </svg>
        <p class="cart-drawer__empty-title">Your cart is empty.</p>
        <p class="cart-drawer__empty-text">You have not added any items yet.</p>
        <a href="/collections/all" class="btn btn--secondary" onclick="closeCartDrawer()">Shop the Collection</a>
      </div>`;
  } else {
    itemsHTML = '<div class="cart-items">';
    items.forEach(item => {
      const imageHTML = item.image
        ? `<img src="${item.image}" alt="${item.product_title}" class="cart-item__image" loading="lazy" width="80" height="80">`
        : `<div class="cart-item__image" style="background:var(--color-border)"></div>`;

      const variantHTML = item.variant_title && item.variant_title !== 'Default Title'
        ? `<span class="cart-item__variant">${item.variant_title}</span>`
        : '';

      itemsHTML += `
        <div class="cart-item" data-key="${item.key}">
          <div class="cart-item__image-wrap">${imageHTML}</div>
          <div class="cart-item__details">
            <span class="cart-item__title">${item.product_title}</span>
            ${variantHTML}
            <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
            <div class="cart-item__bottom">
              <div class="cart-item__qty" aria-label="Quantity for ${item.product_title}">
                <button class="cart-item__qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">
                  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="1" y1="6" x2="11" y2="6"/></svg>
                </button>
                <span class="cart-item__qty-count">${item.quantity}</span>
                <button class="cart-item__qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">
                  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="6" y1="1" x2="6" y2="11"/><line x1="1" y1="6" x2="11" y2="6"/></svg>
                </button>
              </div>
              <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove ${item.product_title} from cart">Remove</button>
            </div>
          </div>
        </div>`;
    });
    itemsHTML += '</div>';
  }

  // Shipping bar
  const shippingText = remaining > 0
    ? `<strong>${formatMoney(remaining)}</strong> away from free shipping`
    : `<span class="shipping-unlocked"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>Free shipping unlocked!</span>`;

  const shippingBarHTML = `
    <div class="cart-drawer__shipping-bar">
      <p class="cart-drawer__shipping-text">${shippingText}</p>
      <div class="cart-drawer__shipping-progress">
        <div class="cart-drawer__shipping-fill" style="width:${progress}%"></div>
      </div>
    </div>`;

  // Footer
  const footerHTML = items.length > 0 ? `
    <div class="cart-drawer__footer">
      <div class="cart-drawer__subtotal">
        <span class="cart-drawer__subtotal-label">Subtotal</span>
        <span class="cart-drawer__subtotal-amount">${formatMoney(subtotal)}</span>
      </div>
      <a href="/checkout" class="cart-drawer__checkout">Checkout</a>
      <a href="/collections/all" class="cart-drawer__continue" onclick="closeCartDrawer()">Continue Shopping</a>
    </div>` : '';

  // Header count label
  const countLabel = itemCount === 1 ? '1 item' : `${itemCount} items`;

  // Re-render header count
  const titleEl = drawer.querySelector('.cart-drawer__title');
  const countEl = drawer.querySelector('.cart-drawer__count');
  if (titleEl) titleEl.textContent = 'Your Cart';
  if (countEl) countEl.textContent = itemCount > 0 ? `(${countLabel})` : '';

  const bodyEl   = drawer.querySelector('.cart-drawer__body');
  const footerEl = drawer.querySelector('.cart-drawer__footer-slot');

  if (bodyEl) {
    bodyEl.innerHTML = shippingBarHTML + itemsHTML;
  }
  if (footerEl) {
    footerEl.innerHTML = footerHTML;
  }

  // Bind quantity and remove events
  bindCartDrawerEvents(drawer);
}

function bindCartDrawerEvents(drawer) {
  // Quantity buttons
  drawer.querySelectorAll('.cart-item__qty-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const key    = btn.dataset.key;
      const action = btn.dataset.action;
      const current = parseInt(btn.dataset.qty, 10);
      const newQty = action === 'increase' ? current + 1 : Math.max(0, current - 1);
      try {
        await changeCartItem(key, newQty);
        renderCartDrawer();
      } catch (err) {
        showNotification(err.message);
      }
    });
  });

  // Remove buttons
  drawer.querySelectorAll('.cart-item__remove').forEach(btn => {
    btn.addEventListener('click', async () => {
      const key = btn.dataset.key;
      try {
        await removeCartItem(key);
        renderCartDrawer();
      } catch (err) {
        showNotification(err.message);
      }
    });
  });
}

function initCartDrawer() {
  const backdrop = getCartBackdrop();
  const drawer   = getCartDrawer();
  if (!backdrop || !drawer) return;

  backdrop.addEventListener('click', closeCartDrawer);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
      closeCartDrawer();
    }
  });

  const closeBtn = drawer.querySelector('.cart-drawer__close');
  if (closeBtn) closeBtn.addEventListener('click', closeCartDrawer);

  // Cart icon opens drawer
  document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      try {
        await fetchCart();
      } catch (err) {
        console.error(err);
      }
      openCartDrawer();
    });
  });
}

/* ─── Header Scroll ─────────────────────────────────────── */

function initHeaderScroll() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  // Header is always solid sticky — just add box-shadow on scroll
  function onScroll() {
    header.classList.toggle('scrolled', window.scrollY > 10);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ─── Mobile Navigation ─────────────────────────────────── */

function initMobileNav() {
  const menuBtn    = document.querySelector('[data-mobile-menu-toggle]');
  const overlay    = document.querySelector('.mobile-nav-overlay');
  const closeBtn   = overlay && overlay.querySelector('[data-mobile-menu-close]');
  if (!menuBtn || !overlay) return;

  menuBtn.addEventListener('click', () => {
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    menuBtn.setAttribute('aria-expanded', 'true');
    if (closeBtn) closeBtn.focus();
  });

  function closeNav() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    menuBtn.setAttribute('aria-expanded', 'false');
    menuBtn.focus();
  }

  if (closeBtn) closeBtn.addEventListener('click', closeNav);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
      closeNav();
    }
  });
}

/* ─── Announcement Bar ──────────────────────────────────── */

function initAnnouncementBar() {
  const bar = document.querySelector('.announcement-bar');
  if (!bar) return;

  const dismissKey = 'mh_announcement_dismissed';
  if (sessionStorage.getItem(dismissKey) === '1') {
    bar.classList.add('is-hidden');
    updateHeaderPosition(false);
    return;
  }

  const closeBtn = bar.querySelector('.announcement-bar__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      bar.classList.add('is-hidden');
      sessionStorage.setItem(dismissKey, '1');
      updateHeaderPosition(false);
    });
  }

  updateHeaderPosition(true);
}

function updateHeaderPosition(announcementVisible) {
  const header = document.querySelector('.site-header');
  if (!header) return;
  header.classList.toggle('has-announcement', announcementVisible);
}

/* ─── Product Page ──────────────────────────────────────── */

function initProductGallery() {
  const mainImage = document.querySelector('.product-gallery__main-image');
  const thumbs    = document.querySelectorAll('.product-gallery__thumb');
  if (!mainImage || !thumbs.length) return;

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      const src    = thumb.dataset.imageSrc;
      const srcset = thumb.dataset.imageSrcset || '';
      const alt    = thumb.dataset.imageAlt || '';

      mainImage.classList.add('is-loading');

      const newImg = new Image();
      newImg.onload = () => {
        mainImage.src    = src;
        mainImage.srcset = srcset;
        mainImage.alt    = alt;
        mainImage.classList.remove('is-loading');
      };
      newImg.src = src;

      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });
}

function initVariantSelector() {
  const form = document.querySelector('[data-product-form]');
  if (!form) return;

  const productJSON = document.getElementById('product-json');
  if (!productJSON) return;

  let product;
  try {
    product = JSON.parse(productJSON.textContent);
  } catch (e) {
    return;
  }

  const variantIdInput = form.querySelector('[name="id"]');
  const priceEl        = form.querySelector('[data-product-price]');
  const comparePriceEl = form.querySelector('[data-product-compare-price]');
  const atcBtn         = form.querySelector('.product-form__atc');
  const soldOutEl      = form.querySelector('.product-form__sold-out');
  const optionBtns     = form.querySelectorAll('.variant-option');

  function getSelectedOptions() {
    const options = {};
    const groups  = form.querySelectorAll('[data-option-group]');
    groups.forEach(group => {
      const name = group.dataset.optionGroup;
      const active = group.querySelector('.variant-option.is-active');
      if (active) options[name] = active.dataset.value;
    });
    return options;
  }

  function findVariant(selectedOptions) {
    return product.variants.find(variant => {
      return variant.options.every((optVal, idx) => {
        const optName = product.options[idx];
        return selectedOptions[optName] === optVal;
      });
    });
  }

  function updateForm(variant) {
    if (!variant) return;

    // Update hidden input
    if (variantIdInput) variantIdInput.value = variant.id;

    // Update price
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }
    if (comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePriceEl.textContent = formatMoney(variant.compare_at_price);
        comparePriceEl.style.display = 'inline';
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.style.display = 'none';
      }
    }

    // Update ATC / sold out state
    const available = variant.available;
    if (atcBtn) atcBtn.style.display    = available ? '' : 'none';
    if (soldOutEl) soldOutEl.style.display = available ? 'none' : '';
    if (atcBtn) {
      atcBtn.disabled = !available;
    }

    // Update mobile sticky bar price
    const stickyPrice = document.querySelector('.mobile-sticky-atc__price');
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  optionBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const group = btn.closest('[data-option-group]');
      if (group) {
        group.querySelectorAll('.variant-option').forEach(b => b.classList.remove('is-active'));
      }
      btn.classList.add('is-active');

      const selected = getSelectedOptions();
      const variant  = findVariant(selected);
      if (variant) updateForm(variant);
    });
  });

  // Init with current variant
  const selectedOptions = getSelectedOptions();
  const currentVariant  = findVariant(selectedOptions);
  if (currentVariant) updateForm(currentVariant);
}

function initProductForm() {
  const form   = document.querySelector('[data-product-form]');
  if (!form) return;

  const atcBtn   = form.querySelector('.product-form__atc');
  const qtyInput = form.querySelector('.quantity-selector__input');
  const minusBtn = form.querySelector('[data-qty-minus]');
  const plusBtn  = form.querySelector('[data-qty-plus]');

  // Quantity +/-
  if (minusBtn && qtyInput) {
    minusBtn.addEventListener('click', () => {
      const val = Math.max(1, parseInt(qtyInput.value, 10) - 1);
      qtyInput.value = val;
    });
  }
  if (plusBtn && qtyInput) {
    plusBtn.addEventListener('click', () => {
      const val = parseInt(qtyInput.value, 10) + 1;
      qtyInput.value = val;
    });
  }

  // ATC submit
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!atcBtn || atcBtn.disabled) return;

    const idInput  = form.querySelector('[name="id"]');
    const variantId = idInput ? parseInt(idInput.value, 10) : null;
    if (!variantId) return;

    const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;

    atcBtn.classList.add('is-loading');
    atcBtn.disabled = true;

    try {
      await addToCart(variantId, qty);
      await fetchCart();
      openCartDrawer();
    } catch (err) {
      showNotification(err.message || 'Could not add to cart');
    } finally {
      atcBtn.classList.remove('is-loading');
      atcBtn.disabled = false;
    }
  });

  // Mobile sticky ATC button
  const stickyBtn = document.querySelector('.mobile-sticky-atc__btn');
  if (stickyBtn) {
    stickyBtn.addEventListener('click', () => {
      form.querySelector('.product-form__atc') && form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    });
  }
}

function initMobileStickyATC() {
  const atcBtn    = document.querySelector('.product-form__atc');
  const stickyBar = document.querySelector('.mobile-sticky-atc');
  if (!atcBtn || !stickyBar) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
    });
  }, { threshold: 0 });

  observer.observe(atcBtn);
}

/* ─── Accordion ─────────────────────────────────────────── */

function initAccordions() {
  const triggers = document.querySelectorAll('.accordion__trigger');
  triggers.forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion__item');
      if (!item) return;
      const isOpen = item.classList.contains('is-open');

      // Close all siblings
      const accordion = item.closest('.accordion');
      if (accordion) {
        accordion.querySelectorAll('.accordion__item.is-open').forEach(openItem => {
          if (openItem !== item) {
            openItem.classList.remove('is-open');
            openItem.querySelector('.accordion__trigger')
              && openItem.querySelector('.accordion__trigger').setAttribute('aria-expanded', 'false');
          }
        });
      }

      item.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });
  });
}

/* ─── Collection Filters ────────────────────────────────── */

function initCollectionFilters() {
  const pills = document.querySelectorAll('.filter-pill[data-tag]');
  if (!pills.length) return;

  pills.forEach(pill => {
    pill.addEventListener('click', (e) => {
      e.preventDefault();
      const tag = pill.dataset.tag;

      pills.forEach(p => p.classList.remove('is-active'));
      pill.classList.add('is-active');

      const grid = document.querySelector('.product-grid');
      if (!grid) return;

      const cards = grid.querySelectorAll('.product-card[data-tags]');

      if (tag === 'all') {
        cards.forEach(card => card.style.display = '');
        return;
      }

      cards.forEach(card => {
        const tags = (card.dataset.tags || '').toLowerCase().split(',').map(t => t.trim());
        card.style.display = tags.includes(tag.toLowerCase()) ? '' : 'none';
      });
    });
  });
}

/* ─── Quick Add ─────────────────────────────────────────── */

function initQuickAdd() {
  document.querySelectorAll('.product-card__quick-add').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.textContent = 'Adding…';
      btn.disabled = true;

      try {
        await addToCart(parseInt(variantId, 10), 1);
        await fetchCart();
        openCartDrawer();
        btn.textContent = 'Added!';
        setTimeout(() => {
          btn.textContent = 'Quick Add';
          btn.disabled = false;
        }, 1500);
      } catch (err) {
        showNotification(err.message || 'Could not add to cart');
        btn.textContent = 'Quick Add';
        btn.disabled = false;
      }
    });
  });
}

/* ─── Email Capture ─────────────────────────────────────── */

function initEmailCapture() {
  document.querySelectorAll('[data-email-form]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      if (!input || !input.value) return;

      const email = input.value.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        showNotification('Please enter a valid email address.');
        return;
      }

      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Subscribing…';
      }

      try {
        // Submit to Shopify customer newsletter form
        const formData = new FormData();
        formData.append('contact[email]', email);
        formData.append('form_type', 'customer');
        formData.append('utf8', '✓');

        await fetch('/contact#ContactFooter', {
          method: 'POST',
          body: formData
        });

        // Show success state
        const section = form.closest('.email-signup, [data-email-section]');
        if (section) section.classList.add('success');

        const successEl = section && section.querySelector('.email-signup__success');
        if (successEl) successEl.style.display = 'block';

      } catch (err) {
        showNotification('Something went wrong. Please try again.');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Subscribe';
        }
      }
    });
  });
}

/* ─── Search Overlay (with predictive search) ───────────── */

function initSearchOverlay() {
  const overlay   = document.getElementById('search-overlay');
  const openBtns  = document.querySelectorAll('[data-search-toggle]');
  const closeBtn  = overlay && overlay.querySelector('[data-search-close]');
  if (!overlay) return;

  const input = overlay.querySelector('.search-overlay__input');
  let resultsEl = overlay.querySelector('.search-results');
  if (!resultsEl) {
    resultsEl = document.createElement('div');
    resultsEl.className = 'search-results';
    resultsEl.setAttribute('role', 'listbox');
    resultsEl.setAttribute('aria-label', 'Search results');
    overlay.appendChild(resultsEl);
  }

  function openSearch() {
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (input) input.focus();
  }

  function closeSearch() {
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    resultsEl.innerHTML = '';
  }

  // Predictive search via Shopify Storefront Search API
  const debouncedSearch = debounce(async (query) => {
    if (!query || query.length < 2) {
      resultsEl.innerHTML = '';
      return;
    }
    try {
      const res = await fetch(
        `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (!res.ok) return;
      const text = await res.text();
      // Parse product suggestions from the section response
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const items = doc.querySelectorAll('[data-search-result]');

      if (items.length) {
        resultsEl.innerHTML = '';
        items.forEach(item => resultsEl.appendChild(item.cloneNode(true)));
        return;
      }

      // Fallback: plain JSON endpoint
      const jsonRes = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`
      );
      if (!jsonRes.ok) return;
      const data = await jsonRes.json();
      const products = data?.resources?.results?.products || [];

      resultsEl.innerHTML = '';
      if (!products.length) {
        resultsEl.innerHTML = `<p style="font-size:0.875rem;color:var(--color-stone);padding:1rem 0;">No results for &ldquo;${query}&rdquo;</p>`;
        return;
      }

      products.forEach(p => {
        const price = p.price ? formatMoney(Math.round(parseFloat(p.price) * 100)) : '';
        const imgSrc = p.image ? p.image : '';
        const el = document.createElement('a');
        el.className = 'search-result-item';
        el.href = p.url;
        el.setAttribute('role', 'option');
        el.innerHTML = `
          ${imgSrc ? `<img src="${imgSrc}" alt="${p.title}" class="search-result__image" loading="lazy" width="56" height="56">` : `<div class="search-result__image" style="background:var(--color-bg-secondary)"></div>`}
          <span class="search-result__title">${p.title}</span>
          <span class="search-result__price">${price}</span>
        `;
        resultsEl.appendChild(el);
      });

    } catch (err) {
      // silent — predictive search is enhancement only
    }
  }, 280);

  if (input) {
    input.addEventListener('input', () => debouncedSearch(input.value.trim()));
  }

  openBtns.forEach(btn => btn.addEventListener('click', (e) => {
    e.preventDefault();
    openSearch();
  }));

  if (closeBtn) closeBtn.addEventListener('click', closeSearch);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeSearch();
  });

  // Click outside closes
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeSearch();
  });
}

/* ─── Product Bundle ────────────────────────────────────── */

function initProductBundle() {
  const bundleForms = document.querySelectorAll('[data-bundle-form]');
  bundleForms.forEach(form => {
    const addBtn   = form.querySelector('[data-bundle-add]');
    const totalEl  = form.querySelector('[data-bundle-total]');
    const checkboxes = form.querySelectorAll('.bundle-item__checkbox');

    function updateTotal() {
      let total = 0;
      checkboxes.forEach(cb => {
        if (cb.checked) {
          total += parseInt(cb.dataset.price || '0', 10);
        }
      });
      if (totalEl) totalEl.textContent = total > 0 ? `Total: ${formatMoney(total)}` : '';
    }

    checkboxes.forEach(cb => cb.addEventListener('change', updateTotal));
    updateTotal();

    if (addBtn) {
      addBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const selected = Array.from(checkboxes)
          .filter(cb => cb.checked && cb.dataset.variantId)
          .map(cb => ({ id: parseInt(cb.dataset.variantId, 10), quantity: 1 }));

        if (!selected.length) {
          showNotification('Select at least one item.');
          return;
        }

        addBtn.disabled = true;
        addBtn.textContent = 'Adding…';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: selected })
          });
          if (!res.ok) throw new Error('Could not add items');
          cartData = await fetchCart();
          openCartDrawer();
        } catch (err) {
          showNotification(err.message || 'Could not add to cart');
        } finally {
          addBtn.disabled = false;
          addBtn.textContent = 'Add Selected to Cart';
        }
      });
    }
  });
}

/* ─── Init all ──────────────────────────────────────────── */

domReady(async () => {
  // Fetch cart on load to set initial count
  try {
    await fetchCart();
    if (cartData) updateCartCount(cartData.item_count);
  } catch (e) {
    console.warn('Cart init error:', e);
  }

  initCartDrawer();
  initHeaderScroll();
  initMobileNav();
  initAnnouncementBar();
  initProductGallery();
  initVariantSelector();
  initProductForm();
  initMobileStickyATC();
  initAccordions();
  initCollectionFilters();
  initQuickAdd();
  initEmailCapture();
  initSearchOverlay();
  initProductBundle();
});
